import React from 'react';
import { useMediaQuery } from '@mui/material';
import { FREE_PACKAGE_CODES } from '@src/constants/package';
import {
  checkCustomPackage,
  checkOneTimePackage,
  getPathPaymentWhenNotifyLowCredits,
} from '@src/services/package';
import WarningIcon from '@src/assets/icons/warning-v2-with-padding.svg';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import sanitizeHtml from 'sanitize-html';
import useTotalCredits from '@src/hooks/useTotalCredits';
import ActionDialog from '@src/new-components/Dialog/ActionDialog';
import CommonButton from '@src/new-components/Button';
import { COLOR } from '@src/styles/color';
import theme from '@src/styles/theme';
import CustomTypography from '@src/new-components/Typography';
import { useMappingPackage } from '@src/hooks/useMappingPackage';
import { StyledNotifyLowCreditsDialogContent } from './index.style';

const HtmlDescription = ({ html }) => (
  <StyledNotifyLowCreditsDialogContent>
    <CustomTypography
      align="center"
      dangerouslySetInnerHTML={{
        __html: sanitizeHtml(html, { allowedTags: ['br'] }),
      }}
      variant="body-14-regular"
      color={COLOR.darkGray}
      style={{ marginTop: '8px' }}
    />
  </StyledNotifyLowCreditsDialogContent>
);

const NotifyLowCredits = ({ open, onClose, usingPackage }) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const history = useHistory();
  const { totalCredits } = useTotalCredits();
  const { packages } = useMappingPackage();

  const isFree = FREE_PACKAGE_CODES.includes(usingPackage?.code);
  const isCustom = checkCustomPackage({
    type: usingPackage?.type,
    level: usingPackage?.level,
  });
  const isOneTime = checkOneTimePackage(usingPackage?.version);

  const getDescKey = () => {
    if (isFree) return 'lowCharactersFreePackageDescription';
    if (isCustom) return 'lowCharactersCustomPackageDescription';
    if (isOneTime) return 'lowCharactersOneTimePackageDescription';
    return 'lowCharactersCyclePackageDescription';
  };

  const getDescription = () => {
    const html = t(getDescKey(), {
      totalCredits: totalCredits.toLocaleString(language),
    });
    return <HtmlDescription html={html} />;
  };

  const getButtonText = () => {
    if (isFree) return 'upgradeNow';
    if (isCustom) return 'renewalNow';
    if (isOneTime) return 'buyNow';
    return 'buyMoreCharacters';
  };

  const renderAction = () => {
    const pathPayment = getPathPaymentWhenNotifyLowCredits(
      usingPackage,
      packages,
    );
    const text = getButtonText();
    return (
      <CommonButton
        customStyle={{
          width: isMobile ? '100%' : 'auto',
          height: '40px',
          borderRadius: '8px',
          marginTop: '16px',
          padding: '10px 24px',
        }}
        textProps={{
          variantText: 'body-14-semibold',
          color: COLOR.darkBlue,
        }}
        onClick={() => history.push(pathPayment)}
      >
        {t(text)}
      </CommonButton>
    );
  };

  return (
    <ActionDialog
      open={open}
      onClose={onClose}
      title={t('runningOutOfCharacters')}
      styleTitle={{ textAlign: 'center' }}
      styleContentWrapper={{ width: '100%', gap: '0px' }}
      contentComponent={getDescription()}
      image={WarningIcon}
      width="512px"
      actionComponent={renderAction()}
    />
  );
};

export default NotifyLowCredits;
